import type { ReactNode } from 'react';
import type { GameType } from '@tv-casino/shared';
import type { GamePollVM } from '@tv-casino/viewmodels/components/gamePollModel.js';
import styles from './GamePollCard.module.css';

interface GamePollCardProps {
  vm: GamePollVM;
  busy?: boolean;
  /** Cast (or move) this phone's ballot. Omitted on a screen that only watches. */
  onVote?: (game: GameType) => void;
  /** Shown under the options — the host's own controls hang here. */
  children?: ReactNode;
}

/**
 * The table's vote on the next game. Each option is a bar that fills with its
 * share of the ballots, so the room can read the standings at a glance; the
 * one this phone backed is outlined, and tapping another moves the vote.
 */
export function GamePollCard({ vm, busy = false, onVote, children }: GamePollCardProps) {
  const votable = vm.canVote && onVote !== undefined;

  return (
    <section className={styles.wrap} aria-label="Game poll">
      <p className={styles.heading}>Table vote</p>
      <p className={styles.title}>{vm.title}</p>
      <p className={styles.meta}>
        {vm.countLine} · {vm.leaderLine}
      </p>

      <div className={styles.options}>
        {vm.rows.map((row) => (
          <button
            key={row.game}
            type="button"
            className={[styles.option, row.mine ? styles.mine : '', row.leading ? styles.leading : '']
              .filter(Boolean)
              .join(' ')}
            disabled={!votable || busy}
            aria-pressed={row.mine}
            onClick={() => onVote?.(row.game)}
          >
            <span className={styles.bar} style={{ width: `${row.percent}%` }} aria-hidden="true" />
            <span className={styles.label}>
              {row.label}
              {row.mine ? ' ✓' : ''}
            </span>
            <span className={styles.count} aria-label={`${row.votes} votes`}>
              {row.votes}
            </span>
          </button>
        ))}
      </div>

      {votable && !vm.myVote ? (
        <p className={styles.hint}>Pick one — the table plays whatever wins.</p>
      ) : null}
      {children}
    </section>
  );
}
